import { Node } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import InfoPricesNodeView from './InfoPricesNodeView.vue'
import { normalizeHexColor } from './colorAttributes'
import { DEFAULT_ACCENT_COLOR, MAX_INFO_PRICES_ROWS, INFO_PRICES_STYLES, infoPricesAccentStyle } from './infoPricesStyles'

const fieldText = (element, field) => (element.querySelector(`[data-field="${field}"]`)?.textContent || '').trim()

const emptyRow = () => ({ label: '', amount: '' })

/**
 * "Informative prices" block: an optional title and 1-6 price rows (label + free-text amount),
 * the first one highlighted with the accent color. No link. Attributes are read back from the
 * visible HTML through data-* markers (no duplicated JSON) and every text goes out as a text
 * node (escaped). A row is only left out when both its label and amount are empty.
 */
export const InfoPricesNode = Node.create({
  name: 'infoPrices',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      title: {
        default: '',
        rendered: false,
        parseHTML: element => fieldText(element, 'title'),
      },
      rows: {
        default: [emptyRow()],
        rendered: false,
        parseHTML: element => {
          const rows = [...element.querySelectorAll('[data-row]')]
            .map(row => ({ label: fieldText(row, 'label'), amount: fieldText(row, 'amount') }))
            .slice(0, MAX_INFO_PRICES_ROWS)
          return rows.length ? rows : [emptyRow()]
        },
      },
      accentColor: {
        default: DEFAULT_ACCENT_COLOR,
        rendered: false,
        parseHTML: element => normalizeHexColor(element.getAttribute('data-accent-color')) || DEFAULT_ACCENT_COLOR,
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="info-prices"]' }]
  },

  renderHTML({ node }) {
    const title = node.attrs.title
    const color = normalizeHexColor(node.attrs.accentColor) || DEFAULT_ACCENT_COLOR
    const rows = (Array.isArray(node.attrs.rows) ? node.attrs.rows : []).slice(0, MAX_INFO_PRICES_ROWS)

    const rowSpecs = rows
      .map((row, index) => {
        const label = row?.label || ''
        const amount = row?.amount || ''
        if (!label && !amount) return null

        const colors = index === 0 ? infoPricesAccentStyle(color) : INFO_PRICES_STYLES.neutralRow
        const parts = []
        if (label) parts.push(['span', { 'data-field': 'label' }, label])
        if (amount) parts.push(['strong', { 'data-field': 'amount' }, amount])
        return ['span', { 'data-row': '', style: INFO_PRICES_STYLES.row + colors }, ...parts]
      })
      .filter(Boolean)

    const children = []
    if (title) {
      children.push(['p', { 'data-field': 'title', style: INFO_PRICES_STYLES.title }, title])
    }
    if (rowSpecs.length) {
      children.push(['div', { style: INFO_PRICES_STYLES.rows }, ...rowSpecs])
    }

    return ['div', { 'data-type': 'info-prices', 'data-accent-color': color, style: INFO_PRICES_STYLES.card }, ...children]
  },

  addNodeView() {
    return VueNodeViewRenderer(InfoPricesNodeView)
  },

  addCommands() {
    return {
      insertInfoPrices: (attrs = {}) => ({ chain }) => {
        return chain()
          .insertContent({
            type: this.name,
            attrs,
          })
          .run()
      },
    }
  },
})

export default InfoPricesNode
